/**
 * <mango-jobs> — the active work-order pipeline screen.
 * =====================================================================
 * Lists the provider's work orders grouped into the prototype's pipeline stages
 * (Offered → Scheduled → Onsite → Equipment Processing → FMV Approval → Mango
 * Reviewing), with the Full pipeline / Needs attention toggle. Offered cards
 * expose accept/decline (the only list-level transitions); every card links to
 * its stage-appropriate detail screen.
 *
 * The whole chain is validated at build time: typed client (from openapi.yaml) →
 * bind.ts convention → Jordan's views/jobs.html (data-bind / data-action only).
 *
 * ROLE-GATING IS COSMETIC (server enforces): the `sow_rate` earnings tag is shown
 * only when the serialized work order actually carries `sow_rate` (Admin); a
 * Staff response omits it and the tag is removed. We gate on field presence.
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field and no bind references it.
 */
import { listWorkOrders, type WorkOrder, type ProviderStatus } from "../api/client";
import { applyBindings, wireActions } from "../bind";
import {
  loadView,
  PIPELINE,
  STAGE_LABEL,
  STATUS_CONFIG,
  ATTENTION,
  detailRoute,
  hasFinancials,
  toggle,
  date,
  money,
  timeLeft,
  slaChip,
  stagePluralLabel,
  setText,
} from "./shared";
import { loadProfile, certsExpiringSoon } from "../profile";

const VIEW_URL = new URL("../../views/jobs.html", import.meta.url).href;

export class MangoJobs extends HTMLElement {
  private disposeActions: (() => void) | null = null;
  private orders: WorkOrder[] = [];
  private view: "pipeline" | "attention" = "pipeline";

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    // accept — fired from the quick-accept modal's Confirm (data-action="accept"),
    // carrying the typed sign-off. getBody returns false (aborts) if no name yet.
    this.disposeActions = wireActions(this, {
      getBody: (ev) => (ev.action === "accept" ? this.qaBody() : undefined),
      onSuccess: (ev) => {
        if (ev.action === "accept") this.closeQaModal();
        void this.reload();
      },
      onError: (ev, error) => {
        console.error(`[mango-jobs] action "${ev.action}" failed for ${ev.id}`, error);
        this.setStatus(`Could not ${ev.action} ${ev.id}. Please retry.`);
      },
    });

    this.wireToggle();
    this.wireCardNavigation();
    this.wireCertDismiss();
    this.wireQuickAccept();
    await this.reload();
  }

  // ── Quick-accept modal (Admin "accept w/ signature") ──────────────────────
  private wireQuickAccept(): void {
    // A card's "Quick accept" opens the modal (delegated — cards re-render on reload).
    const openFromTarget = (target: HTMLElement): void => {
      const btn = target.closest<HTMLElement>("[data-quick-accept]");
      if (!btn) return;
      const id = btn.closest<HTMLElement>("[data-id]")?.dataset.id;
      if (id) this.openQaModal(id);
    };
    this.addEventListener("click", (e) => openFromTarget(e.target as HTMLElement));
    this.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (!(e.target as HTMLElement).closest("[data-quick-accept]")) return;
      e.preventDefault();
      openFromTarget(e.target as HTMLElement);
    });

    // Modal controls are static — wire once. Signature enables Confirm + previews.
    const sig = this.querySelector<HTMLInputElement>("[data-qa-sig]");
    const confirm = this.querySelector<HTMLButtonElement>("[data-qa-confirm]");
    sig?.addEventListener("input", () => {
      const v = sig.value.trim();
      if (confirm) confirm.disabled = !v;
      setText(this, "[data-qa-sig-preview]", v);
    });
    this.querySelector<HTMLElement>("[data-qa-cancel]")?.addEventListener("click", () => this.closeQaModal());
    const overlay = this.querySelector<HTMLElement>("[data-qa-modal]");
    overlay?.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeQaModal();
    });
  }

  private openQaModal(shortName: string): void {
    const job = this.orders.find((j) => j.short_name === shortName);
    const modal = this.querySelector<HTMLElement>("[data-qa-modal]");
    if (!job || !modal) return;
    modal.dataset.id = shortName; // the Confirm's accept resolves its target from here
    setText(this, "[data-qa-site]", job.pickup_region || job.job_reference_id || shortName);
    setText(this, "[data-qa-date]", date(job.dropoff_datetime));
    setText(this, "[data-qa-code]", shortName);
    const rate = job.sow_rate != null ? money(job.sow_rate) : "235.00";
    setText(this, "[data-qa-rate]", rate);
    // Funds-released line (J1): the provider fee released as working capital on
    // acceptance — same figure as the rate. Informational only (no transfer/API).
    setText(this, "[data-qa-funds]", rate);
    // Drive-time ETA (J15): server-computed via Google Distance Matrix; present only
    // when both ends are geocoded and Maps is reachable. Reveal the row only when we
    // actually have a value, so the modal never shows an empty drive-time line.
    const driveTime = job.drive_time_text ?? "";
    const driveRow = this.querySelector<HTMLElement>("[data-qa-drive-row]");
    if (driveRow) driveRow.style.display = driveTime ? "" : "none";
    if (driveTime) setText(this, "[data-qa-drive-time]", driveTime);
    const sig = this.querySelector<HTMLInputElement>("[data-qa-sig]");
    if (sig) sig.value = "";
    setText(this, "[data-qa-sig-preview]", "");
    const confirm = this.querySelector<HTMLButtonElement>("[data-qa-confirm]");
    if (confirm) confirm.disabled = true;
    modal.classList.add("show");
    sig?.focus();
  }

  private closeQaModal(): void {
    this.querySelector<HTMLElement>("[data-qa-modal]")?.classList.remove("show");
  }

  /** The accept body from the modal: the typed sign-off, or false (abort) if empty. */
  private qaBody(): { signoff_name: string } | false {
    const sig = (this.querySelector<HTMLInputElement>("[data-qa-sig]")?.value ?? "").trim();
    return sig ? { signoff_name: sig } : false;
  }

  /** Dismiss the cert-expiry banner (client-side hide; matches the prototype). */
  private wireCertDismiss(): void {
    const hide = (): void => {
      const b = this.querySelector<HTMLElement>("[data-cert-banner]");
      if (b) b.style.display = "none";
    };
    const btn = this.querySelector<HTMLElement>("[data-cert-dismiss]");
    btn?.addEventListener("click", hide);
    btn?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        hide();
      }
    });
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
  }

  private setStatus(message: string): void {
    const el = this.querySelector<HTMLElement>("[data-status]");
    if (el) el.textContent = message;
  }

  /** Toggle between full pipeline and the needs-attention subset. CSP-safe.
   *  Active state painted inline to match the prototype's setJobsView exactly. */
  private wireToggle(): void {
    this.querySelectorAll<HTMLButtonElement>(".jobs-toggle-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const v = btn.dataset.view === "attention" ? "attention" : "pipeline";
        if (v === this.view) return;
        this.view = v;
        this.paintToggle();
        this.render();
      });
    });
    this.paintToggle();
  }

  private paintToggle(): void {
    this.querySelectorAll<HTMLButtonElement>(".jobs-toggle-btn").forEach((b) => {
      const active = b.dataset.view === this.view;
      b.style.background = active ? "var(--accent)" : "transparent";
      b.style.color = active ? "#fff" : "var(--text-2)";
      b.setAttribute("aria-pressed", String(active));
    });
  }

  /**
   * Open the stage-appropriate detail screen when a card (not its accept/decline
   * action) is activated. Click + Enter/Space, so cards are keyboard-operable.
   */
  private wireCardNavigation(): void {
    const go = (target: HTMLElement): void => {
      // An action button (data-action) or the "Quick accept" opener — not navigation.
      if (target.closest("[data-action]") || target.closest("[data-quick-accept]")) return;
      // Action-banner chip → smooth-scroll to its pipeline section (prototype scrollToSection).
      const chip = target.closest<HTMLElement>("[data-scroll-to]");
      if (chip) {
        this.querySelector(`#${chip.dataset.scrollTo}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      const card = target.closest<HTMLElement>("[data-card-link]");
      const id = card?.dataset.id;
      const route = card?.dataset.route;
      if (id && route) window.location.hash = `#/${route}/${encodeURIComponent(id)}`;
    };
    this.addEventListener("click", (e) => go(e.target as HTMLElement));
    this.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const card = (e.target as HTMLElement).closest<HTMLElement>("[data-card-link]");
      if (!card) return;
      if ((e.target as HTMLElement).closest("[data-action]") || (e.target as HTMLElement).closest("[data-quick-accept]")) return;
      e.preventDefault();
      go(e.target as HTMLElement);
    });
  }

  private async reload(): Promise<void> {
    this.setStatus("Loading work orders…");
    try {
      const page = await listWorkOrders({ limit: 100 });
      this.orders = page.results;
    } catch (error) {
      console.error("[mango-jobs] failed to load work orders", error);
      this.setStatus("Could not load work orders.");
      return;
    }
    this.render();
    void this.renderCertBanner();
  }

  /** Show the cert-expiry banner if a verified certification is within 90 days
   *  of expiry (real profile data; hidden otherwise). Best-effort, non-blocking. */
  private async renderCertBanner(): Promise<void> {
    const banner = this.querySelector<HTMLElement>("[data-cert-banner]");
    if (!banner) return;
    try {
      const expiring = certsExpiringSoon(await loadProfile(), 90);
      const soonest = expiring[0];
      if (!soonest) {
        banner.style.display = "none";
        return;
      }
      const textEl = banner.querySelector<HTMLElement>("[data-cert-banner-text]");
      if (textEl) {
        textEl.innerHTML =
          `<strong>${soonest.name}</strong> certification expires in ${soonest.daysLeft} day` +
          `${soonest.daysLeft === 1 ? "" : "s"}.`;
      }
      banner.style.display = "";
    } catch {
      banner.style.display = "none";
    }
  }

  private render(): void {
    const list = this.querySelector<HTMLElement>("[data-list]");
    const sectionTpl = this.querySelector<HTMLTemplateElement>("template[data-section]");
    const rowTpl = this.querySelector<HTMLTemplateElement>("template[data-row]");
    const dividerTpl = this.querySelector<HTMLTemplateElement>("template[data-divider]");
    if (!list || !sectionTpl || !rowTpl) {
      this.setStatus("View template missing.");
      return;
    }

    // Active = everything not yet complete. Group by provider_status.
    const active = this.orders.filter((j) => j.provider_status !== "complete");
    const groups = new Map<ProviderStatus, WorkOrder[]>();
    for (const status of PIPELINE) groups.set(status, []);
    for (const job of active) {
      const g = job.provider_status ? groups.get(job.provider_status) : undefined;
      if (g) g.push(job);
    }

    // Fix 1 — bubble overdue/at-risk to the top of each section. Within every
    // stage group, sort jobs so those with an urgent SLA chip (OVERDUE or ≤2 days
    // remaining) appear before safe/warning ones. This ensures overdue jobs are
    // never buried below the fold inside their stage section.
    const urgencyRank = (job: WorkOrder): number => {
      const chip = slaChip(job.sla_due_at);
      if (!chip) return 2;          // no SLA clock yet → bottom
      if (chip.cls === "urgent") return 0;   // overdue / ≤2 days → top
      if (chip.cls === "warning") return 1;  // ≤5 days → middle
      return 2;                              // safe → bottom
    };
    for (const [, jobs] of groups) {
      jobs.sort((a, b) => urgencyRank(a) - urgencyRank(b));
    }

    const stages = this.view === "attention" ? PIPELINE.filter((s) => ATTENTION.includes(s)) : PIPELINE;

    // Action-required banner (derived purely from the loaded list — no missing data).
    this.renderActionBanner(active, groups);

    list.replaceChildren();
    let dividerShown = false;
    let anyRendered = false;

    for (const status of stages) {
      const jobs = groups.get(status) ?? [];
      if (!jobs.length) continue;
      anyRendered = true;

      // A single "Needs your attention" divider before the first actionable stage.
      if (dividerTpl && !dividerShown && ATTENTION.includes(status)) {
        list.appendChild(dividerTpl.content.cloneNode(true));
        dividerShown = true;
      }

      const section = sectionTpl.content.cloneNode(true) as DocumentFragment;
      const group = section.querySelector<HTMLElement>("[data-section-group]");
      const titleEl = section.querySelector<HTMLElement>("[data-section-title]");
      const countEl = section.querySelector<HTMLElement>("[data-section-count]");
      const sectionList = section.querySelector<HTMLElement>("[data-section-list]");
      if (group) group.id = `section-${status}`;
      if (titleEl) titleEl.textContent = STAGE_LABEL[status];
      // Fix 4 — pluralize the count badge ("1 offer" / "3 offers" etc.).
      if (countEl) countEl.textContent = stagePluralLabel(status, jobs.length);
      // The prototype's elevated green treatment for the Offered stage.
      if (status === "offered" && group) this.paintOfferedSection(group, jobs.length);
      if (sectionList) for (const job of jobs) sectionList.appendChild(this.renderRow(rowTpl, job));
      list.appendChild(section);
    }

    const countPill = this.querySelector<HTMLElement>("[data-count]");
    if (countPill) countPill.textContent = `${active.length} active`;

    // Sidebar nav badge = number of offered work orders awaiting a decision. The
    // badge lives in the app shell (outside this component), so reach it by id.
    const offeredCount = this.orders.filter((j) => j.provider_status === "offered").length;
    const navBadge = document.getElementById("nav-offer-badge");
    if (navBadge) {
      navBadge.textContent = String(offeredCount);
      navBadge.style.display = offeredCount > 0 ? "" : "none";
    }

    if (!anyRendered) {
      this.setStatus(this.view === "attention" ? "Nothing needs your attention right now." : "No active work orders.");
    } else {
      this.setStatus("");
    }
  }

  /** The prototype's green-elevated Offered section header (renderSection:2088). */
  private paintOfferedSection(group: HTMLElement, count: number): void {
    group.style.cssText =
      "background:var(--green-light);border:1px solid var(--green-border);border-radius:var(--r-lg);padding:12px 14px;margin-bottom:4px";
    const hdr = group.querySelector<HTMLElement>(".jobs-section-hdr");
    if (hdr) {
      hdr.style.marginBottom = "10px";
      hdr.innerHTML =
        '<i class="ti ti-bolt" style="font-size:16px;color:var(--green)"></i>' +
        '<span style="font-size:13px;font-weight:700;color:var(--green);text-transform:uppercase;letter-spacing:.06em">' +
        STAGE_LABEL.offered +
        "</span>" +
        '<span style="font-size:11px;font-weight:700;background:var(--green);color:#fff;padding:2px 9px;border-radius:8px;margin-left:4px">' +
        String(count) +
        "</span>";
    }
  }

  /** Build the action-required banner from the loaded list (prototype renderJobs:2024). */
  private renderActionBanner(active: WorkOrder[], groups: Map<ProviderStatus, WorkOrder[]>): void {
    const banner = this.querySelector<HTMLElement>("[data-action-banner]");
    if (!banner) return;
    const actionable = active.filter((j) => j.provider_status && ATTENTION.includes(j.provider_status));
    if (!actionable.length) {
      banner.style.display = "none";
      return;
    }
    const textEl = banner.querySelector<HTMLElement>("[data-action-banner-text]");
    if (textEl) textEl.textContent = `${actionable.length} item${actionable.length > 1 ? "s" : ""} need your attention`;
    const chipsEl = banner.querySelector<HTMLElement>("[data-action-banner-chips]");
    if (chipsEl) {
      const chips: Array<[ProviderStatus, string, string]> = [
        ["offered", "ti-tag", "Accept offer"],
        ["awaiting", "ti-upload", "Upload docs"],
        ["finalizing", "ti-check", "Finalize"],
      ];
      chipsEl.replaceChildren();
      for (const [status, icon, label] of chips) {
        if (!(groups.get(status) ?? []).length) continue;
        const chip = document.createElement("span");
        chip.className = "arb-chip";
        chip.dataset.scrollTo = `section-${status}`;
        chip.tabIndex = 0;
        chip.innerHTML = `<i class="ti ${icon}" style="font-size:11px"></i> ${label}`;
        chipsEl.appendChild(chip);
      }
    }
    banner.style.display = "";
  }

  private renderRow(rowTpl: HTMLTemplateElement, job: WorkOrder): DocumentFragment {
    const fragment = rowTpl.content.cloneNode(true) as DocumentFragment;
    const status = job.provider_status as ProviderStatus | null;
    const cfg = status ? STATUS_CONFIG[status] : undefined;
    const noAction = status === "mango_reviewing";

    const card = fragment.querySelector<HTMLElement>("[data-id]");
    if (card) {
      card.dataset.id = job.short_name;
      card.dataset.route = detailRoute(job);
      if (status) card.classList.add(status);
      // (Mango Reviewing cards stay navigable → read-only detail view; the
      //  prototype left them un-clickable, but providers want to see the details.)
    }

    applyBindings(fragment, { job });
    // Format the bound dates (applyBindings wrote the raw ISO).
    fragment.querySelectorAll<HTMLElement>('[data-bind="job.dropoff_datetime"]').forEach(
      (el) => (el.textContent = date(job.dropoff_datetime)),
    );
    // Card label: pickup city/region (BR#7 safe). Fall back to the Mango job ref
    // when the work order has no office/region on record (so the line is never blank).
    fragment.querySelectorAll<HTMLElement>('[data-bind="job.pickup_region"]').forEach(
      (el) => (el.textContent = job.pickup_region || job.job_reference_id || job.short_name),
    );

    // Per-status icon glyph + colours (prototype statusConfig).
    if (cfg) {
      const iconBox = fragment.querySelector<HTMLElement>("[data-card-icon]");
      const glyph = fragment.querySelector<HTMLElement>("[data-card-glyph]");
      if (iconBox) iconBox.style.background = cfg.bg;
      if (glyph) {
        glyph.classList.add(cfg.icon);
        glyph.style.color = cfg.color;
        glyph.style.fontSize = "17px";
      }
    }

    // Status pill: either the stage CTA or the gray "Mango reviewing" pill.
    const pill = fragment.querySelector<HTMLElement>("[data-status-pill]");
    const pillLabel = fragment.querySelector<HTMLElement>("[data-pill-label]");
    const chevron = fragment.querySelector<HTMLElement>("[data-pill-chevron]");
    if (pill && pillLabel) {
      if (noAction) {
        pill.classList.add("pill-gray");
        pillLabel.innerHTML = '<i class="ti ti-hourglass" style="font-size:12px;margin-right:3px"></i> Mango reviewing';
        chevron?.remove();
      } else if (cfg) {
        pill.classList.add(cfg.pillClass);
        pillLabel.textContent = cfg.cta;
      }
    }

    // Offered: reveal accept/decline + earnings; compute the accept-window chip.
    const offered = status === "offered";
    // Accept/decline are Provider-Admin only (Q6): the server 403s Staff. We gate
    // the buttons on field presence (a Staff response omits sow_rate) so Staff never
    // SEE them — and so never trip the Admin-only 403, which http.ts would otherwise
    // treat as a session gate. The card still navigates to the read-only detail.
    toggle(fragment, "[data-offered-actions]", offered && hasFinancials(job));
    const showRate = offered && hasFinancials(job) && job.sow_rate != null;
    if (showRate) {
      fragment.querySelectorAll<HTMLElement>('[data-bind="job.sow_rate"]').forEach(
        (el) => (el.textContent = money(job.sow_rate)),
      );
    }
    toggle(fragment, "[data-rate-tag]", showRate);

    // Accept-window chip — computed from offer_expires_at (real, exact). Hidden if absent/expired.
    let showAcceptWindow = false;
    if (offered && job.offer_expires_at) {
      const left = timeLeft(job.offer_expires_at);
      if (left) {
        const txt = fragment.querySelector<HTMLElement>("[data-accept-window-text]");
        if (txt) txt.textContent = `Accept within ${left}`;
        showAcceptWindow = true;
      }
    }
    toggle(fragment, "[data-accept-window]", showAcceptWindow);

    // Scheduled: show the pickup-date tag (date only; time-of-day has no field).
    toggle(fragment, "[data-pickup-tag]", status === "scheduled");

    // Continue-SOW affordance for Equipment Processing.
    toggle(fragment, "[data-continue-sow]", status === "awaiting");

    // Needs-tag (Equipment Processing): the still-pending SOW deliverables, from
    // the work order's deliverable ledger. Hidden when nothing is pending / none.
    let showNeeds = false;
    if (status === "awaiting" && Array.isArray(job.deliverables)) {
      // Short labels for the compact "Needs:" chip (matches the prototype's terse
      // CoD/FMV style); fall back to the full display name for anything unmapped.
      const SHORT: Record<string, string> = {
        "Total Weight": "Total Weight",
        "E-Waste Weight": "E-Waste Weight",
        "Asset Inventory": "Inventory",
        "Certificate of Data Destruction": "CoD",
        "Certificate of Responsible E-Waste Disposal": "E-Waste Cert",
      };
      // The E-Waste Disposal CERTIFICATE is OPTIONAL — the required e-waste
      // deliverable is the WEIGHT. Never surface the cert as a "Needs:" item
      // (vendor-portal E2E fix list, SOW form).
      const OPTIONAL_DELIVERABLES = new Set(["Certificate of Responsible E-Waste Disposal"]);
      const pending = job.deliverables
        .filter((d) => d.status !== "complete")
        .filter((d) => !OPTIONAL_DELIVERABLES.has(d.deliverable_type_display ?? ""))
        .map((d) => SHORT[d.deliverable_type_display ?? ""] ?? d.deliverable_type_display)
        .filter(Boolean);
      if (pending.length) {
        const tag = fragment.querySelector<HTMLElement>("[data-needs-tag]");
        if (tag) {
          tag.innerHTML = `<i class="ti ti-alert-circle" style="font-size:10px"></i> Needs: ${pending.join(" · ")}`;
        }
        showNeeds = true;
      }
    }
    toggle(fragment, "[data-needs-tag]", showNeeds);

    // The card-extra row shows only if at least one chip inside it is visible.
    const anyExtra = showRate || showAcceptWindow || status === "scheduled" || showNeeds;
    toggle(fragment, "[data-card-extra]", anyExtra);

    // Fix 3 — standardize urgency countdowns via slaChip() across all pipeline
    // stages that carry sla_due_at (Equipment Processing, FMV Approval, and Onsite
    // once the SLA clock starts). Using the same helper keeps format, colour class,
    // and OVERDUE sentinel consistent — no ad-hoc countdown strings elsewhere.
    const chip =
      (status === "awaiting" || status === "finalizing" || status === "onsite")
        ? slaChip(job.sla_due_at)
        : null;
    if (chip) {
      const el = fragment.querySelector<HTMLElement>("[data-clock-chip]");
      if (el) {
        el.classList.add(chip.cls);
        el.innerHTML = `<i class="ti ti-clock" style="font-size:12px"></i> ${chip.label}`;
        el.style.display = "";
      }
    }
    // (drive-time stays hidden: no routing data yet.)

    return fragment;
  }
}

export function registerMangoJobs(): void {
  if (!customElements.get("mango-jobs")) {
    customElements.define("mango-jobs", MangoJobs);
  }
}
